import React from "react";
import PageHero, { FeatureList } from "@/components/PageHero";
import { CTAFooter } from "@/pages/Prestations";
import PhotoGallery from "@/components/PhotoGallery";
import { useSeo, JsonLd } from "@/lib/seo";
import { Landmark, Music, Flag, HeartHandshake } from "lucide-react";
import { CatalogPackages } from "@/components/CatalogPackages";

const COLL_PHOTOS = [2, 3, 8, 12, 1, 9].map(
  (n) => `/assets/entreprises/entreprise-${String(n).padStart(2, "0")}.jpg`
);

export default function Collectivites() {
  useSeo({
    title: "DJ mairie & collectivité Hauts-de-France | My Animation",
    description:
      "Animation DJ pour mairies, collectivités, Fête de la Musique, 14 juillet, cérémonies, inaugurations et événements publics.",
    path: "/collectivites",
    keywords: "DJ mairie Hauts-de-France, animation Fête de la Musique, animation 14 juillet, DJ collectivité Nord, DJ association",
  });

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "DJ Mairie & Collectivité",
    provider: { "@type": "LocalBusiness", name: "My Animation" },
    areaServed: "Hauts-de-France, Nord, Pas-de-Calais, Belgique",
    description: "Animations pour fêtes municipales, Fête de la Musique, 14 juillet, cérémonies, événements associatifs, inaugurations, soirées publiques et animations locales.",
  };

  return (
    <>
      <JsonLd data={serviceLd} />
      <PageHero
        eyebrow="Collectivités & Mairies"
        title={<>Au service <span className="italic gold-text">de vos événements publics.</span></>}
        subtitle="Animations pour fêtes municipales, Fête de la Musique, 14 juillet, cérémonies, événements associatifs, inaugurations, soirées publiques et animations locales — de Bousbecque à Lille, Tourcoing, Halluin, Roncq, Comines, Wervicq-Sud, Marcq-en-Barœul, Hazebrouck, Béthune, Lens, Arras et Dunkerque."
        img="/assets/entreprises/entreprise-02.jpg"
      />

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 reveal">
            <span className="eyebrow">Notre expertise</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.02]">
              Un prestataire <br/><span className="italic gold-text">de confiance.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 reveal">
            <p className="text-white/70 text-base md:text-lg leading-relaxed">
              My Animation accompagne les mairies, collectivités locales et associations
              pour les fêtes municipales, événements publics et cérémonies. Nous proposons
              une prestation clé en main avec sonorisation professionnelle, micros pour
              discours et animations adaptées au public familial et intergénérationnel.
            </p>
            <div className="mt-10">
              <FeatureList items={[
                "Devis & contrat administratif",
                "Facturation collectivité",
                "Sonorisation extérieure & intérieure",
                "Micros HF pour discours officiels",
                "Animation tous publics",
                "Karaoké & blind test disponibles",
                "Mise en lumière selon le lieu",
                "Intervention dans tout le Nord, Pas-de-Calais et la Belgique",
              ]} />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 bg-[#080808] border-y border-[#DCB457]/15">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-14 reveal">
            <span className="eyebrow">Événements publics</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3">Une réponse <span className="italic gold-text">pour chaque temps fort.</span></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { i: Music, t: "Fête de la Musique", d: "Programmation festive, sonorisation extérieure, scène lumière." },
              { i: Flag, t: "14 juillet", d: "Bal populaire, animation tous publics, transition vers le feu d'artifice." },
              { i: Landmark, t: "Inaugurations & cérémonies", d: "Sonorisation discours, fond musical élégant, mise en lumière." },
              { i: HeartHandshake, t: "Associations & événements locaux", d: "Soutien aux associations locales avec un matériel adapté." },
            ].map(({ i: Icon, t, d }) => (
              <div key={t} className="card-luxe p-7 reveal">
                <Icon size={28} strokeWidth={1} className="text-[#DCB457] mb-5" />
                <h3 className="font-serif-luxe text-2xl">{t}</h3>
                <p className="text-white/55 mt-2 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CatalogPackages
        category="mairie"
        title={<>Nos forfaits <span className="italic gold-text">Collectivité.</span></>}
        subtitle={(n) => `${n || "Nos"} formule${n > 1 ? "s" : ""} pensées pour les mairies et collectivités — de l'inauguration au grand bal populaire.`}
        testidPrefix="collectivite-pack"
        footerNote="Tarifs TTC · TVA non applicable art. 293 B du CGI · Facturation collectivité (mandat administratif accepté)"
      />

      <PhotoGallery
        images={COLL_PHOTOS}
        eyebrow="Nos réalisations"
        title={<>Événements <span className="italic gold-text">publics & municipaux.</span></>}
        subtitle="Quelques temps forts animés par My Animation pour des mairies, collectivités et associations des Hauts-de-France."
        testIdPrefix="coll-gallery"
      />

      <CTAFooter />
    </>
  );
}
